import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import {
  ApiConfirmEmailRequest,
  ApiSetEmailRequest,
  ApiChangeVerificationFlowRequest,
} from '../openapi';
import { addSeconds } from 'date-fns';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { mapResponse } from './helpers';

export function useBusinessKycTokenQuery() {
  const api = useApiClient();
  return useQuery({
    retry: true,
    gcTime: Infinity,
    staleTime: Infinity,
    queryKey: ['businesses', 'current', 'kyb-token'],
    queryFn: () =>
      api
        .POST('/businesses/current/kyb/token', {
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then((res) => {
          if (res.data?.token) {
            return res.data.token;
          }

          throw new Error('Token not found');
        }),
  });
}

export function useBusinessVerificationLinkMutation() {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'verification-link'],
    mutationFn: () =>
      api
        .POST('/businesses/current/kyb/verification-link', {
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
  });
}

export function useIndividualKycTokenQuery() {
  const api = useApiClient();
  return useQuery({
    retry: true,
    gcTime: Infinity,
    staleTime: Infinity,
    queryKey: ['customers', 'current', 'kyc-token'],
    queryFn: () =>
      api
        .POST('/customers/current/kyc/token', {
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then((res) => {
          if (res.data?.token) {
            return res.data.token;
          }

          throw new Error('Token not found');
        }),
  });
}

export function useIndividualVerificationLinkMutation() {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['customers', 'current', 'verification-link'],
    mutationFn: () =>
      api
        .POST('/customers/current/kyc/verification-link', {
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
  });
}

export function useUpdateVerificationFlowTypeMutation({
  idempotencyKey,
}: {
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'kyb-verification-flow', idempotencyKey],
    mutationFn: (body: ApiChangeVerificationFlowRequest) =>
      api
        .POST('/businesses/current/kyb/change-verification-flow', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then((res) => res.data),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['businesses', 'current'],
      }),
  });
}

export function useSetEmailMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'set-email', idempotencyKey],
    mutationFn: (body: ApiSetEmailRequest) =>
      api
        .POST('/businesses/current/email/set', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then((res) => addSeconds(new Date(), res.data?.resendInSec ?? 0)),
  });
}

export function useConfirmEmailMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'confirm-email', idempotencyKey],
    mutationFn: (body: ApiConfirmEmailRequest) =>
      api
        .POST('/businesses/current/email/confirm', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
